import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

// Icons
import {
  ArrowLeft,
  CheckCircle2,
  HelpCircle,
  Info,
  Loader2,
  LockKeyhole,
  Mail,
  MessageCircle,
  Timer,
} from "lucide-react";

// Components
import InputError from "@/components/InputError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";

// API Function
const sendResetPasswordLink = async ({
  email,
  tag,
}: {
  email: string;
  tag?: string;
}) => {
  const response = await api.post("/auth/send-reset-password-link", {
    email,
    lang: localStorage.getItem("lang") ?? "id",
    tag,
  });
  return response.data;
};

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement | null>(null);
  const [searchParams] = useSearchParams();
  const tagParams = searchParams.get("tag") ?? "";

  const [emailSent, setEmailSent] = useState(false);
  // 1. Tambahkan state untuk menyimpan email yang disubmit
  const [submittedEmail, setSubmittedEmail] = useState("");

  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const { mutate, isPending, error } = useMutation({
    mutationFn: sendResetPasswordLink,
    onSuccess: (data) => {
      setEmailSent(true);
      setCooldown(60);
      toast.success(data?.message || "Tautan reset berhasil dikirim.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      setEmailSent(false);
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message ||
        "Terjadi kesalahan yang tidak diketahui.";
      toast.error(errorMessage);
    },
  });

  const handleResetPasswordRequest = (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (cooldown > 0) return;

    // 2. Simpan value dari ref ke dalam state sebelum form unmount
    const emailValue = emailRef.current?.value || "";
    setSubmittedEmail(emailValue);

    mutate({ email: emailValue, tag: searchParams.get("tag") ?? undefined });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950 flex flex-col items-center justify-center p-4 transition-colors duration-300">
      <div className="w-full max-w-md mb-6" onClick={() => navigate(-1)}>
        <div className="inline-flex items-center text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("guest.layout.button.back")}
        </div>
      </div>

      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden transition-colors duration-300">
        <div className="p-8">
          <div className="text-center mb-8">
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                emailSent
                  ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
              }`}
            >
              {emailSent ? (
                <CheckCircle2 className="w-8 h-8" />
              ) : (
                <LockKeyhole className="w-8 h-8" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              {emailSent
                ? t("guest.layout.notif.email-sent.1") || "Email Terkirim!"
                : t("guest.layout.forgot-password.title")}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-2 leading-relaxed">
              {emailSent
                ? t("guest.layout.forgot-password.sent.description")
                : t("guest.layout.forgot-password.description")}
            </p>
          </div>

          {!emailSent ? (
            <div className="space-y-6 animate-in fade-in zoom-in duration-300">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3 shadow-sm">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed">
                  <span className="font-bold block mb-1">
                    {t("guest.layout.forgot-password.notice.title")}
                  </span>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: t(
                        "guest.layout.forgot-password.notice.description",
                      ),
                    }}
                  />
                </div>
              </div>

              <form onSubmit={handleResetPasswordRequest} className="space-y-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="font-semibold text-slate-700 dark:text-slate-200"
                  >
                    {t("guest.layout.forgot-password.label.email")}
                  </Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <Mail className="w-4 h-4" />
                    </div>
                    <Input
                      id="email"
                      ref={emailRef}
                      required
                      placeholder="nama@email.com"
                      type="email"
                      // 3. Tambahkan defaultValue agar jika klik Kirim Ulang, emailnya tidak hilang
                      defaultValue={submittedEmail}
                      className="pl-10 h-11 bg-slate-50 border-slate-200 dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:placeholder:text-slate-500 focus:bg-white dark:focus:bg-slate-900 transition-all"
                    />
                  </div>
                  <InputError
                    text={error?.response?.data.errors?.email?.join(",")}
                  />
                </div>

                <Button
                  className="w-full h-11 text-base font-semibold"
                  disabled={isPending || cooldown > 0}
                >
                  {isPending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>
                        {t("guest.layout.forgot-password.button.processing")}
                      </span>
                    </div>
                  ) : cooldown > 0 ? (
                    <div className="flex items-center gap-2">
                      <Timer className="w-4 h-4" />
                      <span>
                        {t("guest.layout.forgot-password.button.wait", {
                          count: cooldown,
                        })}
                      </span>
                    </div>
                  ) : (
                    t("guest.layout.button.send-reset-passsowrd-request") ||
                    "Kirim Tautan Reset"
                  )}
                </Button>
              </form>
            </div>
          ) : (
            <div className="space-y-6 animate-in fade-in zoom-in duration-300">
              <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-lg text-sm text-slate-600 dark:text-slate-300 text-center border border-slate-100 dark:border-slate-800">
                {t("guest.layout.forgot-password.sent.info")}{" "}
                {/* 4. Tampilkan state submittedEmail, bukan dari emailRef */}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {submittedEmail}
                </span>
              </div>

              <div className="space-y-3">
                <Link to={`/login${tagParams ? `?tag=${tagParams}` : ""}`}>
                  <Button
                    className="w-full h-11 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
                    variant="outline"
                  >
                    {t("guest.layout.forgot-password.button.back-login")}
                  </Button>
                </Link>

                <p className="text-xs text-center text-slate-400 dark:text-slate-500 flex flex-col items-center justify-center gap-1 mt-4">
                  <span>Belum menerima email?</span>
                  {cooldown > 0 ? (
                    <span className="text-slate-400 flex items-center gap-1">
                      <Timer className="w-3.5 h-3.5" /> Tunggu {cooldown}s untuk
                      kirim ulang
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEmailSent(false)}
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      Kirim Ulang
                    </button>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Help Section */}
        <div className="bg-slate-50 dark:bg-slate-950/50 p-6 border-t border-slate-100 dark:border-slate-800 transition-colors">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <HelpCircle className="w-5 h-5 text-slate-400 dark:text-slate-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-200">
                {t("guest.layout.help.title") || "Butuh bantuan?"}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-3">
                {t("guest.layout.help.description") ||
                  "Hubungi layanan bantuan kami jika Anda mengalami kendala teknis."}
              </p>

              <Button
                variant="outline"
                size="sm"
                className="w-full bg-white dark:bg-slate-900 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-800 dark:hover:text-green-300 hover:border-green-300 transition-colors"
                onClick={() => window.open("http://wa.me/6287730060048")}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat WhatsApp Helpdesk
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
